export type ChoirRehearsalRunthroughGuideStepId =
    | "add_lyrics"
    | "upload_reference_audio"
    | "add_custom_harmony"
    | "review_submission"
    | "open_practise"
    | "record_practice_section"
    | "submit_practice";

export interface ChoirRehearsalRunthroughGuideState {
    completed: ChoirRehearsalRunthroughGuideStepId[];
    dismissed_at: string | null;
    completed_at: string | null;
}

export interface ChoirRehearsalRunthroughGuideContext {
    role: "director" | "member";
    needsLyrics?: boolean;
    needsReferenceAudio?: boolean;
    needsCustomHarmony?: boolean;
    canReviewSubmission?: boolean;
}

export const CHOIR_REHEARSAL_RUNTHROUGH_GUIDE_STEPS: ChoirRehearsalRunthroughGuideStepId[] = [
    "add_lyrics",
    "upload_reference_audio",
    "add_custom_harmony",
    "review_submission",
    "open_practise",
    "record_practice_section",
    "submit_practice",
];

export function createDefaultChoirRehearsalRunthroughGuideState(
    seed?: Partial<ChoirRehearsalRunthroughGuideState> | null
): ChoirRehearsalRunthroughGuideState {
    const completed = Array.isArray(seed?.completed)
        ? seed?.completed?.filter((step): step is ChoirRehearsalRunthroughGuideStepId => (
            CHOIR_REHEARSAL_RUNTHROUGH_GUIDE_STEPS.includes(step as ChoirRehearsalRunthroughGuideStepId)
        )) || []
        : [];

    return {
        completed,
        dismissed_at: seed?.dismissed_at ?? null,
        completed_at: seed?.completed_at ?? null,
    };
}

export function getChoirRehearsalRunthroughGuideSteps(
    context: ChoirRehearsalRunthroughGuideContext
): ChoirRehearsalRunthroughGuideStepId[] {
    if (context.role === "member") {
        return [
            "open_practise",
            "record_practice_section",
            "submit_practice",
        ];
    }

    const steps: ChoirRehearsalRunthroughGuideStepId[] = [];

    if (context.needsLyrics) {
        steps.push("add_lyrics");
    }
    if (context.needsReferenceAudio) {
        steps.push("upload_reference_audio");
    }

    if (context.needsCustomHarmony !== false) {
        steps.push("add_custom_harmony");
    }

    if (context.canReviewSubmission) {
        steps.push("review_submission");
    }

    return steps;
}

export function getChoirRehearsalRunthroughGuideNextStep(
    state: ChoirRehearsalRunthroughGuideState,
    context: ChoirRehearsalRunthroughGuideContext
): ChoirRehearsalRunthroughGuideStepId | null {
    const completed = new Set(state.completed || []);
    return getChoirRehearsalRunthroughGuideSteps(context)
        .find((step) => !completed.has(step)) || null;
}

export function isChoirRehearsalRunthroughGuideStepCompleted(
    state: ChoirRehearsalRunthroughGuideState,
    step: ChoirRehearsalRunthroughGuideStepId
): boolean {
    return state.completed.includes(step);
}

export function markChoirRehearsalRunthroughGuideStep(
    state: ChoirRehearsalRunthroughGuideState,
    step: ChoirRehearsalRunthroughGuideStepId,
    activeSteps: ChoirRehearsalRunthroughGuideStepId[] = CHOIR_REHEARSAL_RUNTHROUGH_GUIDE_STEPS
): ChoirRehearsalRunthroughGuideState {
    const relevantSteps = activeSteps.length ? activeSteps : CHOIR_REHEARSAL_RUNTHROUGH_GUIDE_STEPS;
    if (state.completed.includes(step)) {
        const done = relevantSteps.every((candidate) => state.completed.includes(candidate));
        return done && !state.completed_at
            ? {...state, completed_at: new Date().toISOString()}
            : state;
    }

    const nextCompleted = [...state.completed, step];
    const done = relevantSteps.every((candidate) => nextCompleted.includes(candidate));

    return {
        ...state,
        completed: nextCompleted,
        completed_at: done ? state.completed_at || new Date().toISOString() : state.completed_at,
    };
}
